import React, { useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCurrentUser } from '../utils/api';
import { IoPersonAddOutline, IoSettingsOutline, IoHelpCircleOutline, IoStatsChartOutline, IoGameControllerOutline, IoCloseOutline } from 'react-icons/io5';
import './AdminSidebar.css';

// Separate component for the actual sidebar content
const AdminSidebarContent = ({ isOpen, onClose }) => {
  const location = useLocation();

  // Handle escape key press
  const handleEscapeKey = useCallback((event) => {
    if (event.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  // Add escape key listener
  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleEscapeKey]);

  // Close sidebar on route change
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location.pathname, onClose, isOpen]);

  // Handle body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle link click
  const handleLinkClick = (e) => {
    e.stopPropagation(); // Prevent event from bubbling
    onClose(e);
  };

  // Handle close button click
  const handleCloseClick = (e) => {
    e.stopPropagation(); // Prevent event from bubbling
    onClose(e);
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    e.stopPropagation(); // Prevent event from bubbling
    onClose(e);
  };

  // Handle sidebar click
  const handleSidebarClick = (e) => {
    e.stopPropagation(); // Prevent click from reaching overlay
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className={`admin-sidebar-overlay ${isOpen ? 'visible' : ''}`} 
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`admin-sidebar ${isOpen ? 'mobile-open' : ''}`} 
        aria-expanded={isOpen}
        role="complementary"
        aria-label="Admin navigation"
        onClick={handleSidebarClick}
      >
        {/* Mobile close button */}
        <button 
          className="mobile-close-btn" 
          onClick={handleCloseClick} 
          aria-label="Close sidebar"
        >
          <IoCloseOutline className="mobile-close-icon" />
        </button>

        <nav className="admin-sidebar-nav" role="navigation">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Game Management</div>
            <Link 
              to="/que-cards" 
              className={`admin-sidebar-link blue-btn ${location.pathname === '/que-cards' ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <IoStatsChartOutline className="sidebar-icon" />
              <span>Que Cards</span>
            </Link>
            
            <Link 
              to="/gameshow" 
              className={`admin-sidebar-link blue-btn ${location.pathname === '/gameshow' ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <IoGameControllerOutline className="sidebar-icon" />
              <span>Gameshow</span>
            </Link>
          </div>
          
          <div className="sidebar-divider" role="separator"></div>
          
          <div className="sidebar-section">
            <div className="sidebar-section-title">Administration</div>
            <Link 
              to="/admin" 
              className={`admin-sidebar-link admin-btn ${location.pathname === '/admin' ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <IoSettingsOutline className="sidebar-icon" />
              <span>Admin Dashboard</span>
            </Link>
            
            <Link 
              to="/generate-question" 
              className={`admin-sidebar-link admin-btn ${location.pathname === '/generate-question' ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <IoHelpCircleOutline className="sidebar-icon" />
              <span>Generate Question</span>
            </Link>
            
            <Link 
              to="/manage-users" 
              className={`admin-sidebar-link create-user-btn ${location.pathname === '/manage-users' ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <IoPersonAddOutline className="sidebar-icon" />
              <span>Manage Users</span>
            </Link>
          </div>
        </nav>
      </aside>
    </>
  );
};

// Main AdminSidebar component that handles the user check
const AdminSidebar = (props) => {
  const user = getCurrentUser();
  
  if (!user || user.role_id !== 1) {
    return null;
  }

  return <AdminSidebarContent {...props} />;
};

export default AdminSidebar; 